import MessageDotsCircleIcon from '~icons/hawk/message-dots-circle?raw';
import { isEqual, uniqBy } from 'lodash-es';
import { useMapboxFeatureSelection } from '~/common/composables/mapbox/selection';
import { useTerraStore } from '~/terra/store/terra.store';

export function useFeatureSelection({ active_tool, layer_ids, store = useTerraStore() }) {
  function formatFeatures(features) {
    return features.map(feature => ({
      geometry: store.features_hash[feature?.properties?.uid]?.geometry || feature.geometry || feature._geometry,
      id: feature.id,
      properties: feature.properties,
      type: feature.type,
    }));
  }

  const { styleSelection, loadSelectionEvents } = useMapboxFeatureSelection({
    layer_ids,
  }, (e, event_type) => {
    if (event_type === 'ctrlSelect') {
      store.clear_gl_draw();
      const filtered_features = store.selected_features.filter(val => val?.properties?.uid !== e[0]?.properties?.uid);
      if (filtered_features.length === store.selected_features.length)
        filtered_features.push(...formatFeatures(e));
      store.selected_features = filtered_features;
      store.terra_track_events('Feature clicked');
    }
    else if (event_type === 'ctrlShiftSelect') {
      const all_selected_features = [...store.selected_features, ...formatFeatures(e)];
      store.selected_features = uniqBy(all_selected_features, f => f.properties?.uid);
    }
    else {
      if (e.length === 0 && store.draw?.getSelected()?.features?.length !== 0)
        return;

      store.selected_features = uniqBy(e, f => f.properties?.uid).filter(f => ['all_features_source'].includes(f.source) || !f.source).map((item) => {
        if (event_type === 'create' && item.geometry.type === 'Polygon') {
          let key = 'is_polygon';
          if (active_tool.value === 'add-rectangle')
            key = 'is_rectangle';
          else if (active_tool.value === 'add-circle')
            key = 'is_circle';
          item.properties.dataProperties = { [key]: true };
        }
        return {
          geometry: (!['create', 'update', 'selectionchange'].includes(event_type)
            ? store.features_hash[item.properties?.uid]?.geometry
            : null) || item.geometry || item._geometry,
          id: item.id,
          properties: store.features_hash[item.properties?.uid]?.properties || item.properties,
          type: item.type,
        };
      });
    }
    if (event_type === 'create' || (event_type === 'update' && store.selected_features.length === 1)) {
      active_tool.value = '';
      store.features_updated = [...store.features_updated, ...store.selected_features.map(item => item.id)];
    }
    if (event_type === 'clicked')
      store.terra_track_events('Feature clicked');
    handleSelectEvents();
  });

  function handleSelectEvents() {
    const count = store.selected_features.length;
    if (count !== 0) {
      const uid = store.selected_features[0]?.properties?.uid;
      // while creating feature
      if (!uid)
        return;
      const is_geometry_changed = isEqual(
        store.selected_features.map(f => [f.id, f.geometry]),
        store.draw?.getAll()?.features?.map(f => [f.id, f.geometry]),
      );
      store.terra_track_events(
        is_geometry_changed ? 'Features geometry changed' : 'Features selected',
        { count, uid },
      );
    }
  }

  return {
    styleSelection,
    loadSelectionEvents,
    handleSelectEvents,
  };
}

/* -------------------------------------------------------------------------- */
/*                                   Labels                                   */
/* -------------------------------------------------------------------------- */
export function toggleLabels() {
  const terra_store = useTerraStore();
  // TODO Move to store and call on feature update/create
  if (terra_store.map) {
    // TODO add this to the top in the order of layers and remove this

    const sources_and_layers = ['features_labels', 'tasks_labels', 'forms_labels', 'notes_labels'];
    sources_and_layers.forEach((value) => {
      removeSourceAndLayers(terra_store.map, value, value);
    });
  }
  terra_store.map.loadImage('/img/icons/mapbox-text-bg.png', (error, image) => {
    if (error)
      throw error;
    if (!terra_store.map.hasImage('mapbox-text-bg'))
      terra_store.map.addImage('mapbox-text-bg', image, {});

    if (
      (terra_store.settings.display_labels)
      && terra_store.map

    ) {
      // Also toggle task and form labels based on their individual settings
      toggleTaskFormLabels();

      // For features labels
      const featureCollection = {
        type: 'FeatureCollection',
        features: terra_store.features_on_map,
      };
      const pointCollection = terra_store.map.getSource('marker-source');
      if (pointCollection) {
        featureCollection.features = [
          ...featureCollection.features,
          ...pointCollection._data.features,
        ];
      }
      const property_key = terra_store.settings.label_property_key;
      featureCollection.features = featureCollection.features.reduce((acc, f) => {
        let sm_properties = {};
        if (f.properties.name)
          sm_properties = terra_store.sm_instances_map.feature_name_hash[f.properties.name];
        const additional_properties = terra_store.get_feature_additional_properties(f);
        const properties = { ...additional_properties, ...(f.properties.extraProperties || {}), ...sm_properties, name: f.properties.name };
        if (!properties?.[property_key])
          return acc;
        f.properties[property_key] = properties[property_key];
        acc.push(f);
        return acc;
      }, []);

      toggleSymbolLabels({
        map: terra_store.map,
        layerId: 'features_labels',
        sourceId: 'features_labels',
        isEnabled: terra_store.settings.display_labels,
        features: featureCollection.features,
        labelField: property_key,
      });
    }

    // Notes labels require both the top-level "Show labels" setting and their
    // own toggle. Rendered last so it stacks above the feature labels and is
    // not hidden behind them.
    toggleNotesLabels();
  });
}

/* -------------------------------------------------------------------------- */
/*                           Task and Form Labels                            */
/* -------------------------------------------------------------------------- */
export function toggleTaskFormLabels() {
  const terra_store = useTerraStore();

  if (!terra_store.map)
    return;

  const source = terra_store.map.getSource('symbol-source');
  const features_map = (source?._data?.features || []).reduce((acc, feature) => {
    if (feature.properties.name) {
      if (feature.properties.feature_type === 'task') {
        acc.tasks.push(feature);
      }
      else {
        acc.forms.push(feature);
      }
    }
    return acc;
  }, { tasks: [], forms: [] });
  // Toggle task labels
  toggleSymbolLabels({
    map: terra_store.map,
    layerId: 'tasks_labels',
    sourceId: 'tasks_labels',
    isEnabled: terra_store.settings.show_tasks_name,
    features: features_map.tasks,
    labelField: 'name',
  });

  // Toggle form labels
  toggleSymbolLabels({
    map: terra_store.map,
    layerId: 'forms_labels',
    sourceId: 'forms_labels',
    isEnabled: terra_store.settings.show_forms_name,
    features: features_map.forms,
    labelField: 'name',
  });
}

/* -------------------------------------------------------------------------- */
/*                                Notes Labels                                */
/* -------------------------------------------------------------------------- */
export async function toggleNotesLabels() {
  const terra_store = useTerraStore();

  if (!terra_store.map)
    return;

  removeSourceAndLayers(terra_store.map, 'notes_labels', 'notes_labels');

  // Notes labels require the top-level "Show labels" setting and their own
  // toggle to be enabled, and at least one active project. Fetch the notes
  // counts for the active projects before rendering the count labels.
  if (!terra_store.settings.display_labels || !terra_store.settings.show_notes_name || !terra_store.active_projects.length) {
    terra_store.feature_notes_count_map = {};
    terra_store.notes_labels_fetch_key = null;
    return;
  }

  await terra_store.fetch_notes_labels();

  const notes_count_map = terra_store.feature_notes_count_map || {};
  if (!Object.keys(notes_count_map).length)
    return;

  const collection = [...terra_store.features_on_map];
  const point_collection = terra_store.map.getSource('marker-source');
  if (point_collection)
    collection.push(...(point_collection._data?.features || []));

  const turf = (await import('@turf/turf'));

  const notes_features = collection.reduce((acc, feature) => {
    const feature_uid = feature.properties?.uid;
    const notes_count = notes_count_map[feature_uid] || 0;
    const geometry = feature.geometry || terra_store.features_hash[feature_uid]?.geometry;
    if (!notes_count || !geometry)
      return acc;
    const centroid = turf.centroid({ type: 'Feature', geometry, properties: {} });
    if (!centroid?.geometry?.coordinates)
      return acc;
    acc.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: centroid.geometry.coordinates,
      },
      properties: {
        ...feature.properties,
        notes_count: String(notes_count),
      },
    });
    return acc;
  }, []);

  if (!notes_features.length)
    return;

  await ensureNotesIcon(terra_store.map);

  terra_store.map.addSource('notes_labels', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: notes_features,
    },
  });
  terra_store.map.addLayer({
    id: 'notes_labels',
    type: 'symbol',
    source: 'notes_labels',
    layout: {
      'icon-image': 'notes-icon',
      'icon-size': 0.7,
      // Anchor the icon by its bottom so it sits above the feature centroid,
      // keeping the feature label (rendered at the centroid) visible.
      'icon-anchor': 'bottom',
      'icon-offset': [0, -20],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });
  // Keep notes labels on top so they are not hidden behind feature labels.
  terra_store.map.moveLayer('notes_labels');
}

// Registers the notes icon on the map once, so it can be used as the icon-image
// for the notes_labels symbol layer. Uses the same "message-dots-circle" icon
// that the module notes UI uses.
async function ensureNotesIcon(map) {
  if (map.hasImage('notes-icon'))
    return;
  const inner = MessageDotsCircleIcon
    .replace(/currentColor/g, '#475467')
    .replace('<svg', '<svg x="6" y="6"')
    .replace(/\swidth="\d+"/, ' width="20"')
    .replace(/\sheight="\d+"/, ' height="20"');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><rect x="0.75" y="0.75" width="30.5" height="30.5" rx="8" fill="#ffffff" stroke="#D0D5DD" stroke-width="1.5"/>${inner}</svg>`;
  const image = await loadSvgImage(svg, 32, 32);
  if (image && !map.hasImage('notes-icon'))
    map.addImage('notes-icon', image, { pixelRatio: 1 });
}

// Converts an inline SVG string into an HTMLImageElement usable by mapbox addImage.
function loadSvgImage(svg, width, height) {
  return new Promise((resolve) => {
    const img = new Image(width, height);
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}

function removeSourceAndLayers(map, layerId, sourceId) {
  if (map.getLayer(layerId))
    map.removeLayer(layerId);
  if (map.getSource(sourceId))
    map.removeSource(sourceId);
}

function toggleSymbolLabels({ map, layerId, sourceId, isEnabled, features, labelField }) {
  if (isEnabled && features.length > 0) {
    map.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features,
      },
    });
    map.addLayer({
      id: layerId,
      type: 'symbol',

      source: sourceId,
      paint: {
        'text-color': '#202',
      },
      layout: {
        'icon-size': 1.2,
        'icon-image': 'mapbox-text-bg',
        'icon-text-fit': 'both',
        'text-justify': 'center',
        'text-anchor': 'center',
        'text-field': ['get', `${labelField}`],
        'text-font': ['literal', ['Arial Unicode MS Regular']],
        'text-size': 10,
      },
    });
  }
}

// Handle toggling of projects and groups
export async function processViewConfig(config, terra_store) {
  if (config?.active_groups?.length) {
    const container_copy = terra_store.container || {};
    const toggleGroup = async (uid) => {
      terra_store.active_group_map[uid].ortho_enabled = true;
      terra_store.active_group_map[uid].features_enabled = true;

      terra_store.projects_request_status.show = true;
      // Update group data for features and
      const group = await terra_store.set_group({ group: terra_store.container.groups[uid], container: terra_store.container });

      terra_store.projects_request_status = {
        total: 0,
        current: 0,
        cancel: false,
        show: false,
      };
      // Replace the group in container with updated group
      container_copy.groups[uid] = group;
    };
    await Promise.all(config.active_groups.map(group_uid => toggleGroup(group_uid)));
    terra_store.set_container({ container: container_copy });
  }
  if (config?.filtered_active_projects?.length) {
    const projects = [];
    config.filtered_active_projects.forEach((project_uid) => {
      const project = terra_store.active_projects_data_map({ all_projects: true })[project_uid];
      terra_store.active_projects_map[project.uid] = {
        ortho_enabled: true,
        features_enabled: true,
      };
      projects.push(project);
    });
    await terra_store.set_projects_essentials({ projects });
  }
}
